// /eventos — Lista con FiltersSheet integrado (FASE 2)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Calendar as CalendarIcon, SlidersHorizontal, Check } from 'lucide-react';
import { COLORS, fmtDayPretty, WEEKDAY_SHORT, dateKey } from './designSystem';
import { EventsService } from './EventsService';
import { Chip, BottomSheet, EventRow, BottomNav, PhoneShell } from './components';
import { DayCalendar } from './DayCalendar';
import { FiltersSheet, filtersFromParams, filtersToParams } from './FiltersSheet';
import { getStoredAge } from './userStore';

const DAY_STRIP_SIZE = 12;

export default function EventsListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // La flecha de atrás siempre vuelve a Home — Home es la página padre natural;
  // los cambios de día/filtros no deben consumir historial.
  const goBack = () => navigate('/');
  const [events, setEvents] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [daysWith, setDaysWith] = useState(new Set());
  const [daySheet, setDaySheet] = useState(false);
  const [filtersSheet, setFiltersSheet] = useState(false);

  const fechaParam = params.get('fecha');
  const q = params.get('q') || '';
  const currentFilters = filtersFromParams(params);

  const activeDate = useMemo(() => {
    if (!fechaParam) { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
    const parts = fechaParam.split('-');
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    d.setHours(0, 0, 0, 0);
    return d;
  }, [fechaParam]);
  const activeKey = dateKey(activeDate);

  const paramsStr = params.toString();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const current = filtersFromParams(new URLSearchParams(paramsStr));
      const list = await EventsService.getEvents({
        fecha: activeKey,
        texto: q,
        filtros: { ...current, miEdad: getStoredAge() || 18 },
      });
      let filtered = list;
      if (current.cuando) {
        filtered = list.filter((e) => {
          if (!e.fecha_inicio) return true;
          const h = new Date(e.fecha_inicio).getHours();
          if (current.cuando === 'tarde') return h >= 17 && h <= 21;
          if (current.cuando === 'noche') return (h >= 22 && h <= 23) || (h >= 0 && h <= 2);
          if (current.cuando === 'madrugada') return h >= 3 && h <= 7;
          return true;
        });
      }
      if (current.precioMax === 0) filtered = filtered.filter((e) => (e.price || 0) === 0);
      if (cancelled) return;
      setEvents(filtered);
      const all = await EventsService.getEvents({ texto: q });
      if (cancelled) return;
      setAllEvents(all);
      const s = new Set(); for (const e of all) if (e.fecha_inicio) s.add(e.fecha_inicio.slice(0, 10));
      setDaysWith(s);
    })();
    const onAge = () => { setParams((p) => new URLSearchParams(p)); };
    window.addEventListener('clubbers:age-change', onAge);
    return () => { cancelled = true; window.removeEventListener('clubbers:age-change', onAge); };
  }, [paramsStr, activeDate, q, setParams]);

  const strip = useMemo(() => {
    const d0 = new Date(); d0.setHours(0, 0, 0, 0);
    return Array.from({ length: DAY_STRIP_SIZE }, (_, i) => { const d = new Date(d0); d.setDate(d.getDate() + i); return d; });
  }, []);

  // `replace: true` → no se añade entrada al historial al cambiar día/filtros,
  // así la flecha atrás no re-recorre los días pulsados.
  const setDate = (d) => setParams(filtersToParams(currentFilters, { fecha: dateKey(d), ...(q ? { q } : {}) }), { replace: true });

  const applyFilters = (f) => { setParams(filtersToParams(f, { ...(fechaParam ? { fecha: fechaParam } : {}), ...(q ? { q } : {}) }), { replace: true }); setFiltersSheet(false); };

  // Quick-chip: Gratis = precioMax 0, Techno toggle, Mi edad toggle
  const toggleGratis = () => applyFilters({ ...currentFilters, precioMax: currentFilters.precioMax === 0 ? 50 : 0 });
  const toggleTechno = () => applyFilters({ ...currentFilters, estilos: currentFilters.estilos.includes('Techno') ? currentFilters.estilos.filter((s) => s !== 'Techno') : [...currentFilters.estilos, 'Techno'] });
  const toggleEdad = () => {
    const age = getStoredAge();
    if (!currentFilters.soloMiEdad && !age) { navigate('/perfil/edad'); return; }
    applyFilters({ ...currentFilters, soloMiEdad: !currentFilters.soloMiEdad });
  };

  return (
    <PhoneShell>
      <header className="px-5 pt-6 pb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <button onClick={goBack} className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: COLORS.surface }} data-testid="events-back"><ChevronLeft size={22} color={COLORS.text} /></button>
          <div>
            <h1 className="text-white text-xl font-extrabold">Eventos en Madrid</h1>
            <p className="text-sm capitalize" style={{ color: COLORS.textMuted }}>{fmtDayPretty(activeDate)}</p>
          </div>
        </div>
        <button onClick={() => setDaySheet(true)} className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: COLORS.surface }} data-testid="events-open-calendar"><CalendarIcon size={18} color={COLORS.text} /></button>
      </header>

      <div className="flex gap-2.5 overflow-x-auto no-scrollbar px-5 mb-4">
        {strip.map((d) => {
          const k = dateKey(d);
          const dow = (d.getDay() + 6) % 7;
          const todayK = dateKey(new Date());
          const tomorrowK = (() => { const dd = new Date(); dd.setDate(dd.getDate() + 1); return dateKey(dd); })();
          const label = k === todayK ? 'HOY' : k === tomorrowK ? 'MAÑ' : WEEKDAY_SHORT[dow].slice(0, 3);
          const active = k === activeKey;
          const has = daysWith.has(k);
          return (
            <button key={k} onClick={() => setDate(d)} className="flex flex-col items-center justify-center w-14 h-16 rounded-2xl shrink-0" style={{ backgroundColor: active ? COLORS.primary : COLORS.surface, border: `1px solid ${active ? COLORS.primary : COLORS.border}` }} data-testid={`day-chip-${k}`}>
              <span className="text-[10px] font-bold mb-0.5" style={{ color: active ? '#fff' : COLORS.textMuted }}>{label}</span>
              <span className="text-xl font-extrabold" style={{ color: active ? '#fff' : COLORS.text }}>{d.getDate()}</span>
              {has && <span className="w-1 h-1 rounded-full mt-0.5" style={{ backgroundColor: active ? '#fff' : COLORS.primary }} />}
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 px-5 mb-5 overflow-x-auto no-scrollbar">
        <Chip icon={<SlidersHorizontal size={13} />} onClick={() => setFiltersSheet(true)} data-testid="filters-open">Filtros</Chip>
        <Chip variant="ageDark" active={currentFilters.soloMiEdad} icon={<Check size={13} />} onClick={toggleEdad} data-testid="chip-miedad">Mi edad +{getStoredAge() || 18}</Chip>
        <Chip active={currentFilters.estilos.includes('Techno')} onClick={toggleTechno} data-testid="chip-techno">Techno</Chip>
        <Chip active={currentFilters.precioMax === 0} onClick={toggleGratis} data-testid="chip-gratis">Gratis</Chip>
      </div>

      <section className="px-5">
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-white text-lg font-bold capitalize">Esta noche <span className="text-sm font-medium ml-1" style={{ color: COLORS.textMuted }}>· {fmtDayPretty(activeDate)}</span></h2>
          <span className="text-xs font-semibold" style={{ color: COLORS.textMuted }}>{events ? events.length : '—'} eventos</span>
        </div>
        {events === null ? (
          <p className="text-sm" style={{ color: COLORS.textMuted }}>Cargando…</p>
        ) : events.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-white font-semibold mb-1">Nada por aquí</p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>No encontramos eventos con esos filtros.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {events.map((e) => (<EventRow key={e.id} event={e} onClick={() => navigate(`/eventos/${e.id}`)} />))}
          </div>
        )}
      </section>

      <BottomSheet open={daySheet} onClose={() => setDaySheet(false)} title="Elige un día" testid="events-day-sheet">
        <DayCalendar onSelect={(d) => { setDaySheet(false); setDate(d); }} />
      </BottomSheet>

      <FiltersSheet
        open={filtersSheet}
        onClose={() => setFiltersSheet(false)}
        initial={currentFilters}
        onApply={applyFilters}
        allEventsForCount={allEvents.filter((e) => {
          // Pre-filter by current date + text so N shown in button matches base scope
          const okDate = !fechaParam || !e.fecha_inicio || e.fecha_inicio.slice(0, 10) === fechaParam;
          return okDate;
        })}
      />

      <BottomNav />
    </PhoneShell>
  );
}
